/**
 * Card "Financeiro" — mensalidade, vencimento, últimos 5 pagamentos
 * e botão "Registrar" que abre o PaymentModal.
 *
 * Props:
 *   - studentId: id do aluno (alimenta o hook usePayments)
 *   - personalId: id do personal logado (usado ao inserir pagamento)
 *   - monthlyFee: mensalidade atual (vem do student.monthly_fee). Pode ser null.
 *   - dueDay: dia do mês do vencimento. Pode ser null (→ assume 10).
 *
 * Estado interno:
 *   - `payModalVisible`: controla o PaymentModal (dono do estado dos campos)
 *
 * Decisões:
 *   - Mostra só os 5 pagamentos mais recentes — histórico completo
 *     fica em outra tela (não implementada ainda).
 *   - Alert pra confirmar exclusão (pagamento é registro contábil,
 *     vale a fricção).
 *   - `getDaysLate` calcula localmente em vez de vir do banco — view
 *     pode ficar desatualizada se mudar `due_date` no banco, mas pra
 *     nosso caso (visão imediata, pós-insert) tá ok.
 */

import { memo, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, Alert, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { usePayments } from '@/hooks/usePayments';
import { computeDueDate, getDaysLate } from './helpers';
import { PaymentModal } from './PaymentModal';

interface Props {
  studentId: string;
  personalId: string;
  monthlyFee: number | null;
  dueDay: number | null;
}

function FinanceSectionImpl({ studentId, personalId, monthlyFee, dueDay }: Props) {
  const { payments, loading, addPayment, deletePayment } = usePayments(studentId);
  const [modalVisible, setModalVisible] = useState(false);

  const defaultAmount = monthlyFee != null ? String(monthlyFee) : '';

  async function handleSubmit(data: { amount: number; method: any; paidAt: string; notes: string | undefined }) {
    try {
      await addPayment({
        personal_id: personalId,
        amount: data.amount,
        method: data.method,
        paid_at: new Date(data.paidAt + 'T12:00:00').toISOString(),
        notes: data.notes,
        due_date: computeDueDate(data.paidAt, dueDay),
      });
      setModalVisible(false);
    } catch {
      Alert.alert('Erro', 'Não foi possível registrar o pagamento.');
      throw new Error('add-payment-failed'); // propaga pro PaymentModal manter aberto
    }
  }

  function handleDelete(id: string) {
    Alert.alert('Excluir pagamento', 'Confirmar exclusão?', [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Excluir', style: 'destructive', onPress: () => deletePayment(id) },
    ]);
  }

  return (
    <View style={styles.section}>
      <View style={styles.header}>
        <Text style={styles.title}>Financeiro</Text>
        <TouchableOpacity
          style={styles.registerBtn}
          onPress={() => setModalVisible(true)}
          activeOpacity={0.7}
        >
          <Ionicons name="add" size={14} color="#fff" />
          <Text style={styles.registerBtnText}>Registrar</Text>
        </TouchableOpacity>
      </View>

      {monthlyFee != null && (
        <Text style={styles.feeLabel}>
          Mensalidade: R$ {monthlyFee.toFixed(2).replace('.', ',')} · vence dia {dueDay ?? 10}
        </Text>
      )}

      {loading ? (
        <ActivityIndicator color="#C8102E" style={{ marginVertical: 12 }} />
      ) : payments.length === 0 ? (
        <Text style={styles.empty}>Nenhum pagamento registrado</Text>
      ) : (
        payments.slice(0, 5).map(p => {
          const daysLate = getDaysLate(p.paid_at, p.due_date);
          return (
            <View key={p.id} style={styles.payRow}>
              <View style={styles.payLeft}>
                <Text style={styles.payAmount}>R$ {p.amount.toFixed(2).replace('.', ',')}</Text>
                <Text style={styles.payMethod}>{p.method}</Text>
                {p.due_date && (
                  <Text style={[styles.payTag, daysLate ? styles.payTagLate : styles.payTagOk]}>
                    {daysLate ? `● ${daysLate} dias de atraso` : '✓ Pago em dia'}
                  </Text>
                )}
              </View>
              <Text style={styles.payDate}>
                {new Date(p.paid_at).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: '2-digit', year: '2-digit',
                })}
              </Text>
              <TouchableOpacity
                onPress={() => handleDelete(p.id)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="trash-outline" size={16} color="#C8102E" />
              </TouchableOpacity>
            </View>
          );
        })
      )}

      <PaymentModal
        visible={modalVisible}
        defaultAmount={defaultAmount}
        onClose={() => setModalVisible(false)}
        onSubmit={handleSubmit}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  section: { marginBottom: 20 },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  registerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#C8102E',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  registerBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  feeLabel: { fontSize: 12, color: '#888', marginBottom: 8 },
  empty: { fontSize: 12, color: '#888', fontStyle: 'italic' },
  payRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  payLeft: { flex: 1 },
  payAmount: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  payMethod: { fontSize: 11, color: '#888', marginTop: 1, textTransform: 'capitalize' },
  payTag: { fontSize: 10, marginTop: 2 },
  payTagOk: { color: '#2E7D32' },
  payTagLate: { color: '#C8102E' },
  payDate: { fontSize: 12, color: '#888', marginRight: 12 },
});

export const FinanceSection = memo(FinanceSectionImpl);
